import DataGridFilters from "./DataGridFilters/DataGridFilters";
import Stack from "@mui/material/Stack";
import { DataGrid } from "@mui/x-data-grid";
import { useStyles } from "hooks/hooks";
import { useTranslations } from "hooks/UseTranslations";
import styles from "./DataGridTable.module.scss";

export interface TableNameItemsInterface {
    id: number;
    label: string;
    link: string;
}
interface Props {
    tableName: string;
    tableNameItems?: Array<TableNameItemsInterface>;
    data: Array<any>;
    tableColumns: Array<any>;
    getRowId: any;
    handleSearch: any;
    filterOptions: Array<string>;
    setPageColumn: Function;
    columnsPerPage: number;
    setOrder: Function;
    order: string;
    pageSize?: number;
    placeholder: string;
    seeHistoryLink?: string;
    statuses?: Array<string>;
    setStatus?: Function;
    status?: string;
    emptyStateMsg?: string;
}

export default function DataGridTable({
    data,
    tableColumns,
    tableName,
    tableNameItems,
    handleSearch,
    filterOptions,
    setPageColumn,
    columnsPerPage,
    setOrder,
    order,
    seeHistoryLink,
    placeholder,
    statuses,
    setStatus,
    status,
    emptyStateMsg,
    ...props
}: Props) {
    const { translations } = useTranslations();

    const classes = useStyles({
        dataGrid: {
            letterSpacing: "0px !important",
            border: "none !important",
            opacity: "1 !important",
            font: "normal normal normal 13px/21px PolySans Median !important",
            color: "#11131F !important",

            "& p": {
                marginTop: "0 !important",
                textAlign: "left"
            },
            "&.MuiDataGrid-root .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F5F6FA !important"
            },
            "&.MuiDataGrid-root .MuiDataGrid-columnHeaderTitle": {
                font: "normal normal 700 14px/18px PolySans Median !important",
                color: "#8094AE",
                opacity: 1
            },
            "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus": {
                outline: "none"
            },
            "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within": {
                outline: "none"
            },
            "&.MuiDataGrid-root .MuiDataGrid-cell": {
                borderBottom: "none !important",
                maxHeight: "fit-content !important"
            },
            "&.MuiDataGrid-root .MuiDataGrid-cell:focus": {
                outline: "none"
            },
            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                outline: "none"
            },
            "&.MuiDataGrid-root .MuiDataGrid-iconSeparator": {
                display: "none"
            },
            "&.MuiDataGrid-root .MuiDataGrid-row:hover": {
                backgroundColor: "transparent !important"
            },
            "&.MuiDataGrid-root .MuiDataGrid-row": {
                borderBottom: "1px solid rgba(224, 224, 224, 1)",
                maxHeight: "fit-content !important"
            },
            "&.MuiDataGrid-root .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "#F9FAFC !important"
            },
            "&.MuiDataGrid-root .MuiDataGrid-columnHeaderTitleContainer": {
                padding: "0px !important"
            },
            "&.MuiDataGrid-root .MuiDataGrid-virtualScrollerContent": {
                height: "100% !important",
                minHeight: "140px !important"
            },
            "&.MuiDataGrid-root .MuiDataGrid-virtualScrollerRenderZone": {
                position: "relative !important"
            }
        }
    });

    function NoRowsOverlay() {
        return (
            <Stack height="100%" alignItems="center" justifyContent="center" fontSize="20px" color="#8094AE">
                {emptyStateMsg || "No rows"}
            </Stack>
        );
    }

    return (
        <div className={styles.dataGrid}>
            <DataGridFilters
                placeholder={placeholder}
                tableName={tableName}
                tableNameItems={tableNameItems}
                handleSearch={handleSearch}
                filterOptions={filterOptions}
                setPageColumn={setPageColumn}
                columnsPerPage={columnsPerPage}
                setOrder={setOrder}
                seeHistoryLink={seeHistoryLink}
                order={order}
                statuses={statuses}
                setStatus={setStatus}
                status={status}
            />
            <DataGrid
                className={classes.dataGrid}
                columns={tableColumns}
                rows={data || []}
                disableColumnFilter
                disableSelectionOnClick
                disableColumnMenu
                autoHeight={true}
                rowHeight={70}
                onPageSizeChange={(pageSize) => setPageColumn(pageSize)}
                pageSize={columnsPerPage}
                rowsPerPageOptions={[5, 10, 15]}
                components={{ NoRowsOverlay }}
                componentsProps={{
                    pagination: {
                        labelRowsPerPage: translations("label.rowsPerPage")
                    }
                }}
                {...props}
            />
        </div>
    );
}
